import React, {useEffect, useRef} from "react";
import {Box, Button, Flex, IconButton, Text, VStack} from "@chakra-ui/react";
import {MdDelete} from "react-icons/md";
import {usePreviewQC} from "../../context/usePreviewQC.ts";
import {hasUnsavedQCNoteEdits} from "../../context/qcNoteRows.ts";
import {canSaveQCNote} from "../../models/constants/qcProblemTags.ts";
import QCNoteTextarea, {type QCNoteTextareaHandle} from "./QCNoteTextarea.tsx";
import QCProblemTagSelect from "./QCProblemTagSelect.tsx";

const prefersReducedMotion = (): boolean =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

interface QCNotesBodyProps {
  languageCode: string;
  canAddNotes: boolean;
  scrollTargetNoteId?: string | null;
  onScrollTargetHandled?: () => void;
}

const QCNotesBody: React.FC<QCNotesBodyProps> = ({languageCode, canAddNotes, scrollTargetNoteId = null, onScrollTargetHandled}) => {
  const {getNotes, notesError, notesLoading, updateNoteText, setNoteProblemTag, saveNote, deleteNote, seekToNoteTimecode} = usePreviewQC();
  const notes = getNotes(languageCode);
  const textareaRefs = useRef<Map<string, QCNoteTextareaHandle>>(new Map());
  const noteCardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (scrollTargetNoteId === null) return;
    const card = noteCardRefs.current.get(scrollTargetNoteId);
    if (!card) return;
    card.scrollIntoView({block: "nearest", behavior: prefersReducedMotion() ? "auto" : "smooth"});
    onScrollTargetHandled?.();
  }, [notes, onScrollTargetHandled, scrollTargetNoteId]);

  if (notesLoading) return <Text color="gray.400" fontSize="sm">Loading QC notes...</Text>;

  if (!canAddNotes) {
    return (
      <VStack align="stretch" spacing={4}>
        {notesError && <Text role="alert" color="red.300" fontSize="sm">{notesError}</Text>}
        {notesError === null && notes.length === 0 && <Text color="gray.400" fontSize="sm">No QC notes for this language.</Text>}
        {notes.map((note) => (
          <Box key={note.id} border="1px solid" borderColor={note.saved ? "brandPurple.500" : "gray.600"} rounded="xl" p={3}>
            <Flex align="center" justify="space-between" mb={2} gap={2}>
              <Text as="button" type="button" aria-label={`Seek to ${note.timecodeLabel}`} color="brandPurple.300" fontSize="sm" fontWeight="bold" cursor="pointer" textAlign="left" display="inline-flex" alignItems="center" justifyContent="flex-start" minW="40px" minH="40px" sx={{fontVariantNumeric: "tabular-nums"}} _hover={{color: "brandPurple.200", textDecoration: "underline"}} _active={{transform: "scale(0.96)"}} transitionProperty="color, transform" transitionDuration="0.2s" onClick={() => seekToNoteTimecode(note.timecode)}>{note.timecodeLabel}</Text>
              {note.problemTag && <Text color="gray.300" fontSize="sm">{note.problemTag}</Text>}
            </Flex>
            <Text color="white" whiteSpace="pre-wrap">{note.text}</Text>
          </Box>
        ))}
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      {notesError && <Text role="alert" color="red.300" fontSize="sm">{notesError}</Text>}
      {notesError === null && notes.length === 0 && <Text color="gray.400" fontSize="sm">Pause the video and press + to add a note at the current time.</Text>}
      {notes.map((note) => (
        <Box
          key={note.id}
          ref={(el: HTMLDivElement | null) => {
            if (el) noteCardRefs.current.set(note.id, el);
            else noteCardRefs.current.delete(note.id);
          }}
          border="1px solid"
          borderColor={note.saved ? "brandPurple.500" : "gray.600"}
          rounded="xl"
          p={3}
        >
          <Flex align="center" justify="space-between" mb={2} gap={2}>
            <Flex align="center" gap={4} flexShrink={0}>
              <Text as="button" type="button" color="brandPurple.300" fontSize="sm" fontWeight="bold" cursor="pointer" textAlign="left" display="inline-flex" alignItems="center" justifyContent="flex-start" minW="40px" minH="40px" sx={{fontVariantNumeric: "tabular-nums"}} _hover={{color: "brandPurple.200", textDecoration: "underline"}} _active={{transform: "scale(0.96)"}} transitionProperty="color, transform" transitionDuration="0.2s" onClick={() => seekToNoteTimecode(note.timecode)}>
                {note.timecodeLabel}
              </Text>
              <IconButton aria-label="Delete note" icon={<MdDelete size={18} />} size="xs" variant="ghost" color="red.400" minW="40px" minH="40px" _hover={{bg: "gray.700", color: "red.300"}} _active={{transform: "scale(0.96)"}} transitionProperty="background-color, color, transform" transitionDuration="0.2s" onClick={() => deleteNote(languageCode, note.id)} />
            </Flex>
            <Flex align="center" flex="1" minW={0} justify="center" px={1}>
              <Box w="86.4%" minW="6.5rem" maxW="12.25rem">
                <QCProblemTagSelect
                  isClearable={!note.saved}
                  isDisabled={note.saving}
                  selectedTag={note.problemTag}
                  onSelectTag={(tag) => {
                    const previousTag = note.problemTag;
                    if (textareaRefs.current.get(note.id)?.applyProblemTag(previousTag, tag)) {
                      setNoteProblemTag(languageCode, note.id, tag);
                    }
                  }}
                  onClearTag={() => {
                    const previousTag = note.problemTag;
                    if (textareaRefs.current.get(note.id)?.applyProblemTag(previousTag, null)) {
                      setNoteProblemTag(languageCode, note.id, null);
                    }
                  }}
                />
              </Box>
            </Flex>
            <Button size="xs" px={5} minW="40px" minH="40px" flexShrink={0} bg="brandPurple.500" color="white" _hover={{bg: "brandPurple.600"}} _active={{transform: "scale(0.96)"}} transitionProperty="background-color, transform" transitionDuration="0.2s" isDisabled={note.saving || !canSaveQCNote(note.text, note.problemTag) || !hasUnsavedQCNoteEdits(note)} onClick={() => void saveNote(languageCode, note.id)}>
              {note.saving ? "Saving..." : "Save"}
            </Button>
          </Flex>
          <QCNoteTextarea
            ref={(el) => {
              if (el) textareaRefs.current.set(note.id, el);
              else textareaRefs.current.delete(note.id);
            }}
            rounded="md"
            value={note.text}
            onValueChange={(text) => updateNoteText(languageCode, note.id, text)}
            placeholder="Write your note..."
            bg="gray.700"
            color="white"
            borderColor="gray.600"
            isReadOnly={note.saving}
            _placeholder={{color: "gray.400"}}
            _hover={{borderColor: note.saving ? "gray.600" : "brandPurple.400"}}
            _focus={{borderColor: "brandPurple.500", boxShadow: "0 0 0 1px var(--chakra-colors-brandPurple-500)"}}
          />
        </Box>
      ))}
    </VStack>
  );
};

export default QCNotesBody;
